<template>
  <div v-if="open" class="edit-profile-backdrop" @click.self="close">
    <div class="edit-profile" role="dialog" aria-modal="true">
      <header class="edit-profile__header">
        <h2>{{ $t("profile.edit.title") }}</h2>
        <button
          type="button"
          class="edit-profile__close"
          :aria-label="$t('common.dismiss')"
          @click="close"
        >×</button>
      </header>

      <p v-if="kidModeNotice" class="edit-profile__kid-notice" role="status">
        {{ kidModeNotice }}
      </p>

      <SettingsCard :header="$t('profile.edit.identitySection')">
        <div class="edit-profile__field">
          <label for="ep-name" class="edit-profile__label">{{ $t("profile.edit.name") }}</label>
          <input
            id="ep-name"
            v-model="name"
            type="text"
            class="edit-profile__input"
            :placeholder="$t('profile.edit.namePlaceholder')"
            maxlength="40"
          />
        </div>
        <div class="edit-profile__field">
          <label for="ep-age" class="edit-profile__label">{{ $t("profile.edit.age") }}</label>
          <input
            id="ep-age"
            v-model.number="age"
            type="number"
            min="13"
            max="120"
            class="edit-profile__input"
          />
        </div>
      </SettingsCard>

      <SettingsCard :header="$t('profile.edit.idSection')">
        <SettingsRow
          icon-name="clipboard"
          icon-bg="rgba(94, 92, 230, 0.5)"
          :label="$t('profile.edit.copyId')"
          :value="userIdShort"
          @click="copyId"
        />
      </SettingsCard>

      <div class="edit-profile__footer">
        <button
          type="button"
          class="edit-profile__cancel"
          :disabled="busy"
          @click="close"
        >
          {{ $t("profile.edit.cancel") }}
        </button>
        <button
          type="button"
          class="edit-profile__save"
          :disabled="busy || !dirty"
          @click="save"
        >
          <span v-if="saving" class="edit-profile__spinner" aria-hidden="true"></span>
          {{ saving ? $t("profile.edit.saving") : $t("profile.edit.save") }}
        </button>
      </div>

      <div
        v-if="showUnder13Confirm"
        class="edit-profile-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="edit-profile-under13-title"
        @click.self="showUnder13Confirm = false"
      >
        <div class="edit-profile-confirm__dialog">
          <h3 id="edit-profile-under13-title">{{ $t("profile.edit.under13ConfirmTitle") }}</h3>
          <p>{{ $t("profile.edit.under13ConfirmMessage") }}</p>
          <div class="edit-profile-confirm__actions">
            <button
              type="button"
              class="edit-profile__cancel"
              :disabled="busy"
              @click="showUnder13Confirm = false"
            >
              {{ $t("profile.edit.under13ConfirmCancel") }}
            </button>
            <button
              type="button"
              class="edit-profile__danger"
              :disabled="busy"
              @click="deleteUnder13Account"
            >
              <span v-if="deleting" class="edit-profile__spinner" aria-hidden="true"></span>
              {{ deleting ? $t("profile.delete.deleting") : $t("profile.edit.under13ConfirmDelete") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { authState, signOut } from "../stores/authStore";
import { showSnack } from "../stores/snackbarStore";
import { MIN_AGE, getAgeBracket } from "../utils/trackingPolicy";
import { deviceLinkID } from "../utils/pairing";
import SettingsCard from "./SettingsCard.vue";
import SettingsRow from "./SettingsRow.vue";

const DELETE_USER_DATA_URL = "https://us-central1-revisio-ai.cloudfunctions.net/deleteUserData";

export default {
  name: "EditProfileSheet",
  components: { SettingsCard, SettingsRow },
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ["close"],
  data() {
    return {
      name: "",
      age: 18,
      saving: false,
      deleting: false,
      showUnder13Confirm: false,
      origName: "",
      origAge: 18,
    };
  },
  computed: {
    busy() {
      return this.saving || this.deleting;
    },
    user() {
      return authState.userData;
    },
    userIdShort() {
      const id = authState.currentUser ? authState.currentUser.uid : "";
      return id ? id.slice(0, 8) + "…" : "";
    },
    dirty() {
      return this.name !== this.origName || Number(this.age) !== Number(this.origAge);
    },
    kidModeNotice() {
      const u = this.user;
      if (!u || typeof u.age !== "number") return "";
      return getAgeBracket(u.age) === "kidMode" ? this.$t("privacy.kidMode.notice") : "";
    },
  },
  watch: {
    open(v) {
      if (v) this.hydrate();
    },
  },
  methods: {
    hydrate() {
      const u = this.user || {};
      this.name = typeof u.name === "string" ? u.name : "";
      this.age = typeof u.age === "number" ? u.age : 18;
      this.origName = this.name;
      this.origAge = this.age;
    },
    close() {
      if (this.busy) return;
      this.$emit("close");
    },
    async copyId() {
      const id = authState.currentUser ? authState.currentUser.uid : "";
      if (!id) return;
      try {
        await navigator.clipboard.writeText(id);
        showSnack(this.$t("profile.edit.idCopied"), { variant: "success" });
      } catch (err) {
        console.warn("copy id failed", err);
        showSnack(this.$t("profile.edit.idCopyFailed"), { variant: "error" });
      }
    },
    async save() {
      const uid = authState.currentUser ? authState.currentUser.uid : null;
      if (!uid) return;
      const ageChanged = Number(this.age) !== Number(this.origAge);
      const ageValue = Number(this.age);
      if (ageChanged) {
        if (!Number.isFinite(ageValue) || ageValue <= 0 || ageValue > 120) {
          showSnack(this.$t("profile.edit.saveFailed"), { variant: "error" });
          return;
        }
        if (ageValue < MIN_AGE) {
          this.showUnder13Confirm = true;
          return;
        }
      }
      this.saving = true;
      try {
        const update = {};
        if (this.name !== this.origName) update.name = this.name.trim();
        if (ageChanged) update.age = ageValue;
        if (Object.keys(update).length > 0) {
          await updateDoc(doc(db, "users", uid), update);
        }
        showSnack(this.$t("profile.edit.saved"), { variant: "success" });
        this.$emit("close");
      } catch (err) {
        console.error("save profile failed", err);
        showSnack(this.$t("profile.edit.saveFailed"), { variant: "error" });
      } finally {
        this.saving = false;
      }
    },
    async deleteUnder13Account() {
      const cu = authState.currentUser;
      if (!cu) return;
      this.deleting = true;
      try {
        const token = await cu.getIdToken();
        const deviceID = deviceLinkID();
        const res = await fetch(DELETE_USER_DATA_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ deviceID, uid: cu.uid, reason: "under13_profile_age_update" }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || "delete failed");
        }
        showSnack(this.$t("profile.delete.success"), { variant: "success" });
        await signOut().catch(() => null);
        this.showUnder13Confirm = false;
        this.$emit("close");
        this.$router.replace("/");
      } catch (err) {
        console.error("delete under-13 account failed", err);
        showSnack(this.$t("profile.delete.failed"), { variant: "error" });
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.edit-profile-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 14px;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

@media (min-width: 720px) {
  .edit-profile-backdrop {
    align-items: center;
  }
}

.edit-profile {
  width: min(540px, 100%);
  max-height: 85vh;
  overflow-y: auto;
  background: var(--app-surface-strong);
  border: 1px solid var(--app-border);
  border-radius: 22px 22px 0 0;
  box-shadow: var(--app-shadow);
  padding: 18px 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

@media (min-width: 720px) {
  .edit-profile {
    border-radius: 22px;
  }
}

.edit-profile__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.edit-profile__header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--app-heading);
}

.edit-profile__close {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-text);
  font-size: 18px;
  cursor: pointer;
}

.edit-profile__kid-notice {
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-text);
  font-size: 13px;
  line-height: 1.4;
}

.edit-profile__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
}

.edit-profile__field + .edit-profile__field {
  border-top: 1px solid var(--app-border);
}

.edit-profile__label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: var(--app-text-muted);
  text-transform: uppercase;
}

.edit-profile__input {
  height: 40px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-text);
  font-size: 15px;
  font-weight: 500;
}

.edit-profile__footer {
  display: flex;
  gap: 10px;
  padding-top: 6px;
  justify-content: flex-end;
}

.edit-profile__cancel,
.edit-profile__save {
  height: 38px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-text);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.edit-profile__save {
  background: #ffe20e;
  color: #1a1a1a;
  border-color: #ffe20e;
}

.edit-profile__save:disabled,
.edit-profile__cancel:disabled {
  opacity: 0.55;
  cursor: default;
}

.edit-profile__danger {
  height: 38px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid #dc2626;
  background: #dc2626;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.edit-profile__danger:disabled {
  opacity: 0.6;
  cursor: default;
}

.edit-profile__spinner {
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ep-spin 0.8s linear infinite;
}

.edit-profile-confirm {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.edit-profile-confirm__dialog {
  width: min(420px, 100%);
  border: 1px solid var(--app-border);
  border-radius: 16px;
  background: var(--app-surface-strong);
  box-shadow: var(--app-shadow);
  padding: 20px;
}

.edit-profile-confirm__dialog h3 {
  margin: 0 0 10px;
  color: var(--app-heading);
  font-size: 18px;
}

.edit-profile-confirm__dialog p {
  margin: 0;
  color: var(--app-text);
  line-height: 1.45;
}

.edit-profile-confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

@media (max-width: 420px) {
  .edit-profile-confirm__actions {
    flex-direction: column-reverse;
  }

  .edit-profile-confirm__actions button {
    width: 100%;
  }
}

@keyframes ep-spin {
  to { transform: rotate(360deg); }
}
</style>
